import { PHASES } from '../constants';
import { revealHand, endTurn } from './turnActions';
import { buyCard, levelUpHero } from './villageActions';
import { ascendHeroToLevel4 } from '../village/leveling';
import { equipWeapon, unequipWeapon, chooseMonster } from './dungeonActions';
import { resumeCombatIfReady, describeCombatOutcome } from '../combat/resolveCombat';
import { prepare } from './prepareActions';
import { rest } from './restActions';
import {
  useVillageAbility, useDungeonAbility, useSpoilsAbility, resolveTrophyEffects, resolveCalderaDragonDiscard,
  resolveCircleOfProtectionPlacement, resolveUnstableCrystalsCascade, useSmugglerReact,
} from './abilityActions';
import { takeFamiliar, useFamiliarAbility } from './familiarActions';
import {
  resolveHandChoice, resolveOptionalDiscard, resolveEffectChoice, resolveShieldBearerOffer, resolveIntelligentReactOffer,
  resolvePreparePick, resolveOptionalCurseSelfDestroy, dropDeadEntries,
} from './handChoiceActions';
import { spendXpToken } from './xpTokenActions';
import { getInstanceDefinition } from '../cards/cardLookup';
import { computeHandGoldValue, hasActiveForcedFightObligation, hasAssignedMonsterObligation } from '../state/selectors';
import { hasAnyFightableMonster } from '../dungeon/fightEligibility';
import {
  resolveVillageVisitHallEffects, resolveDungeonVisitHallEffects, hasUnmetBanditObligation,
} from '../cards/effectRegistry/monsters';
// See the equivalent comment in state/createGameState.js.
import '../cards/effectRegistry/registerAll';

export const ACTION_TYPES = {
  VISIT_VILLAGE: 'VISIT_VILLAGE',
  BUY_CARD: 'BUY_CARD',
  LEVEL_UP_HERO: 'LEVEL_UP_HERO',
  ASCEND_HERO: 'ASCEND_HERO',
  USE_VILLAGE_ABILITY: 'USE_VILLAGE_ABILITY',
  END_VILLAGE_TURN: 'END_VILLAGE_TURN',
  ENTER_DUNGEON: 'ENTER_DUNGEON',
  EQUIP_WEAPON: 'EQUIP_WEAPON',
  UNEQUIP_WEAPON: 'UNEQUIP_WEAPON',
  USE_DUNGEON_ABILITY: 'USE_DUNGEON_ABILITY',
  CHOOSE_MONSTER: 'CHOOSE_MONSTER',
  USE_SPOILS_ABILITY: 'USE_SPOILS_ABILITY',
  USE_SMUGGLER_REACT: 'USE_SMUGGLER_REACT',
  TAKE_FAMILIAR: 'TAKE_FAMILIAR',
  USE_FAMILIAR_ABILITY: 'USE_FAMILIAR_ABILITY',
  END_DUNGEON_TURN: 'END_DUNGEON_TURN',
  PREPARE: 'PREPARE',
  REST: 'REST',
  RESOLVE_CALDERA_DRAGON_DISCARD: 'RESOLVE_CALDERA_DRAGON_DISCARD',
  RESOLVE_HAND_CHOICE: 'RESOLVE_HAND_CHOICE',
  RESOLVE_OPTIONAL_DISCARD: 'RESOLVE_OPTIONAL_DISCARD',
  RESOLVE_EFFECT_CHOICE: 'RESOLVE_EFFECT_CHOICE',
  RESOLVE_SHIELD_BEARER_OFFER: 'RESOLVE_SHIELD_BEARER_OFFER',
  RESOLVE_INTELLIGENT_REACT_OFFER: 'RESOLVE_INTELLIGENT_REACT_OFFER',
  RESOLVE_PREPARE_PICK: 'RESOLVE_PREPARE_PICK',
  RESOLVE_OPTIONAL_CURSE_DESTROY: 'RESOLVE_OPTIONAL_CURSE_DESTROY',
  SPEND_XP_TOKEN: 'SPEND_XP_TOKEN',
};

function assertActivePlayer(state, playerId) {
  const active = state.players[state.meta.activePlayerIndex];
  if (active.id !== playerId) throw new Error(`It is not ${playerId}'s turn.`);
}

function assertPhase(state, ...allowed) {
  if (!allowed.includes(state.meta.phase)) {
    throw new Error(`Action not allowed in phase "${state.meta.phase}" (expected one of: ${allowed.join(', ')}).`);
  }
}

// Caverns of Bane, Charm of Venery: "When this card is present, you may not
// visit the village." A hand-presence lockout on VISIT_VILLAGE itself - no
// ToR card gates this action, so there's no existing precedent to reuse.
function assertMayVisitVillage(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const blocked = player.hand.some((id) => getInstanceDefinition(state, id).id === 'village-charm-of-venery');
  if (blocked) throw new Error('Charm of Venery: you may not visit the village while this card is present.');
}

// Caverns of Bane, Curse of War: "You cannot rest or prepare." Same
// hand-presence-lockout shape as Charm of Venery above, just gating the
// other two actions instead of Visit the Village.
function assertMayRestOrPrepare(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const blocked = player.hand.some((id) => getInstanceDefinition(state, id).id === 'curse-war');
  if (blocked) throw new Error('Curse of War: you cannot rest or prepare while this curse is in your hand.');
}

function assertNoForcedFightObligation(state, playerId) {
  if (hasActiveForcedFightObligation(state, playerId)) {
    throw new Error('Troll Skullcracker: you must enter the dungeon and fight it first.');
  }
}

// Root of Corruption, Mind Control: "On that player's turn, that player must
// enter the dungeon and attack that monster if able." Same 3 call sites as
// assertNoForcedFightObligation above (Visit/Prepare/Rest all blocked,
// leaving Enter the Dungeon as the only legal top-level action).
function assertNoAssignedMonsterObligation(state, playerId) {
  if (hasAssignedMonsterObligation(state, playerId)) {
    throw new Error('Mind Control: you must enter the dungeon and attack your assigned monster first.');
  }
}

// Human-readable play-by-play, surfaced in GameLog.vue so a human opponent
// (or someone watching the AI play) can see what actually happened without
// reverse-engineering state diffs. Capped so it doesn't grow unbounded over a
// long game / bloat the remote-sync payload.
const MAX_LOG_ENTRIES = 200;

function playerName(state, playerId) {
  return state.players.find((p) => p.id === playerId)?.name || playerId;
}

function titleFor(state, instanceId) {
  return getInstanceDefinition(state, instanceId).title;
}

// Numeric turnFlags worth surfacing when they change - not every field (e.g.
// usedAbilityInstanceIds/leveledHeroInstanceIds are bookkeeping, not "a stat
// changed"). bonusGold is deliberately excluded here - it's already folded
// into the "gold" figure below via computeHandGoldValue, so listing it
// separately would double-report the same change under two labels.
const TURN_FLAG_DELTA_LABELS = {
  bonusPhysicalAttack: 'Physical Attack',
  bonusMagicAttack: 'Magic Attack',
  bonusTotalAttack: 'Attack',
  bonusLight: 'Light',
  bonusBuys: 'bonus buy(s)',
  handSizeReduction: 'hand-size reduction',
};

function signed(n) {
  return `${n > 0 ? '+' : ''}${n}`;
}

// Matt: "it would be nice if there was more detail in the log... spelled
// out some of the steps... when you use dungeon abilities, and when stats
// were modified... useful right now for tracking down bugs." Rather than
// hand-writing a bespoke description for every individual card ability (a
// combinatorial task on the order of the whole card-audit effort), this
// diffs `state` against `nextState` per player and reports whatever
// actually changed - stat bonuses, gold, XP, and which specific cards moved
// zones - regardless of which handler produced the change. Doesn't attempt
// to know the printed VERB an effect used (e.g. a leveled-up hero's old
// copy technically "levels up," not "discards") - only the zone transition
// is knowable generically, so wording is approximate by design; this is a
// debugging aid, not a rules-accurate narration.
function summarizePlayerDelta(state, nextState, playerId, { skipXp = false } = {}) {
  const prev = state.players.find((p) => p.id === playerId);
  const next = nextState.players.find((p) => p.id === playerId);
  if (!prev || !next) return null;
  const parts = [];

  // CHOOSE_MONSTER's own message already states XP gained on a win - only
  // suppressed there, not for other XP-granting actions (familiar/trophy
  // abilities), where it's the only place that information appears at all.
  const xpDelta = next.xp - prev.xp;
  if (xpDelta !== 0 && !skipXp) parts.push(`${signed(xpDelta)} XP`);

  const goldDelta = computeHandGoldValue(nextState, playerId) - computeHandGoldValue(state, playerId);
  if (goldDelta !== 0) parts.push(`${signed(goldDelta)} gold`);

  for (const [key, label] of Object.entries(TURN_FLAG_DELTA_LABELS)) {
    const delta = (next.turnFlags[key] || 0) - (prev.turnFlags[key] || 0);
    if (delta !== 0) parts.push(`${signed(delta)} ${label}`);
  }

  const prevHand = new Set(prev.hand);
  const nextHand = new Set(next.hand);
  const prevDiscard = new Set(prev.discard);
  const nextDiscard = new Set(next.discard);
  const drawn = next.hand.filter((id) => !prevHand.has(id));
  const leftHand = prev.hand.filter((id) => !nextHand.has(id));
  const discardedFromHand = leftHand.filter((id) => nextDiscard.has(id));
  const destroyed = leftHand.filter((id) => !nextDiscard.has(id));
  // Landed in discard without ever being in hand this step (e.g. a curse
  // gained directly, a bought card) - BUY_CARD/LEVEL_UP_HERO's own message
  // already names this card, but the generic diff can't tell that, so a
  // little redundancy there is an acceptable tradeoff for catching curses
  // and other silent discard-pile additions everywhere else.
  const gainedInDiscardRaw = next.discard.filter((id) => !prevDiscard.has(id) && !leftHand.includes(id));
  // Matt: "the Plunderwing enemy has a battle effect that makes you reveal a
  // card from your deck... if it did in fact do the ability I don't
  // remember it." Its handler (revealTopAddGoldToHealth) can destroy the
  // revealed card straight from the deck (never touching hand OR discard) -
  // the only zone transition this generic diff didn't already catch. A card
  // that left the deck and isn't accounted for by a draw or a discard
  // addition was destroyed there instead.
  const nextDeck = new Set(next.deck);
  const leftDeck = prev.deck.filter((id) => !nextDeck.has(id));
  // Matt and Brian both reported this directly, about two different
  // monsters (Huntsman's "reveal the top card of the active player's deck",
  // and an unnamed one with a similar reveal-and-maybe-destroy shape):
  // "I couldn't tell if it actually revealed the top of my deck and did its
  // ability... it didn't show up in the log." It technically did - a
  // revealTopOfOwnDeck-based effect's card lands in gainedInDiscard (or
  // destroyedFromDeck below) exactly like any other card, so its name was
  // always present - but worded identically to "bought a card"/"gained a
  // curse"/"destroyed from hand", with zero indication a reveal ability had
  // even fired. Every revealTopOfOwnDeck call leaves its card's ORIGIN zone
  // as this player's own `deck` (never their hand) - the one zone-transition
  // shape that only a reveal-type effect (never a buy, a curse grant, or a
  // hand-based discard/destroy) produces - so cards found in `leftDeck` are
  // unambiguously reveals, regardless of which handler produced them, and
  // get their own explicit "revealed"/"revealed and destroyed" wording
  // instead of folding into the generic "added .../destroyed ..." used for
  // everything else.
  const revealedKept = leftDeck.filter((id) => gainedInDiscardRaw.includes(id));
  const revealedDestroyed = leftDeck.filter((id) => !nextHand.has(id) && !gainedInDiscardRaw.includes(id));
  const gainedInDiscard = gainedInDiscardRaw.filter((id) => !revealedKept.includes(id));

  const listOf = (ids, refState) => ids.map((id) => titleFor(refState, id)).join(', ');
  if (revealedKept.length) parts.push(`revealed ${listOf(revealedKept, nextState)}`);
  if (revealedDestroyed.length) parts.push(`revealed and destroyed ${listOf(revealedDestroyed, state)}`);
  if (drawn.length) parts.push(`drew ${listOf(drawn, nextState)}`);
  if (discardedFromHand.length) parts.push(`discarded ${listOf(discardedFromHand, state)}`);
  if (destroyed.length) parts.push(`destroyed ${listOf(destroyed, state)}`);
  if (gainedInDiscard.length) parts.push(`added ${listOf(gainedInDiscard, nextState)} to discard`);

  return parts.length ? parts.join(', ') : null;
}

// Only the action types where something can happen that ISN'T already fully
// named by describeAction's own message - a single card-move action
// (BUY_CARD, LEVEL_UP_HERO, REST, RESOLVE_HAND_CHOICE,
// RESOLVE_CALDERA_DRAGON_DISCARD, ...) doesn't need this, since the delta
// would just restate the same single card move a second time.
// EQUIP_WEAPON is the exception that moved INTO the list: Matt reported "I
// just equipped a cursed mace but I'm not sure that it gave me a curse" -
// it did (dungeonActions' gainCurse call), but the curse lands silently in
// discard and the equip line never said so. An ordinary equip moves no
// cards and produces a null delta, so only the Cursed Mace's "(added
// Disease of ... to discard)" ever actually renders. Abilities/
// Trophy-effect-triggering actions/combat do need it, since their actual
// effects are otherwise invisible in the log. RESOLVE_OPTIONAL_DISCARD is
// the one "resolve" action kept here - its own message says "lost XP
// instead" without saying how much; the delta confirms the real amount
// (relevant since XP floors at 0). END_VILLAGE_TURN/END_DUNGEON_TURN are
// deliberately excluded entirely - routine end-of-turn hand cycling
// (discard the rest, draw a fresh 6) would drown out anything actually
// worth seeing.
const DELTA_ENRICHED_ACTIONS = new Set([
  'VISIT_VILLAGE', 'ENTER_DUNGEON', 'USE_VILLAGE_ABILITY', 'USE_DUNGEON_ABILITY',
  'USE_SPOILS_ABILITY', 'USE_SMUGGLER_REACT', 'USE_FAMILIAR_ABILITY', 'CHOOSE_MONSTER', 'PREPARE', 'REST',
  'RESOLVE_OPTIONAL_DISCARD', 'RESOLVE_OPTIONAL_CURSE_DESTROY', 'EQUIP_WEAPON',
]);

function describeStateDelta(state, nextState, type, actingPlayerId) {
  if (!DELTA_ENRICHED_ACTIONS.has(type)) return null;
  const skipXp = type === ACTION_TYPES.CHOOSE_MONSTER;
  const fragments = [];
  const own = summarizePlayerDelta(state, nextState, actingPlayerId, { skipXp });
  if (own) fragments.push(own);
  for (const player of nextState.players) {
    if (player.id === actingPlayerId) continue;
    const other = summarizePlayerDelta(state, nextState, player.id, { skipXp });
    if (other) fragments.push(`${playerName(nextState, player.id)}: ${other}`);
  }
  return fragments.length ? fragments.join('; ') : null;
}

// Numenera, Magma Wyrm: "specialEffects: After a player visits the village,
// this card switches ranks with the monster in front of it." Matt: "I have
// not seen it actually do that" - the swap itself is correct (confirmed by
// direct testing - effectRegistry/monsters.js's VILLAGE_VISIT_HALL_EFFECTS),
// but had zero visible feedback, so an instant silent swap looked like
// nothing happened. Detected here as a plain before/after dungeonHall diff
// rather than threading a description back from
// resolveVillageVisitHallEffects itself - nothing else touches dungeonHall
// during VISIT_VILLAGE (Trophy/Circle of Protection resolution, both called
// earlier in the same dispatch, are hand/discard-only), so any 2-rank swap
// found here is unambiguously this effect. Correctly silent once the Wyrm
// reaches rank 1 (the front) - "the monster in front of it" has nothing
// left to swap with there, per the card's own text, not a bug.
function describeMagmaWyrmSwap(state, nextState) {
  const prevHall = state.dungeonHall;
  const nextHall = nextState.dungeonHall;
  const changedRanks = prevHall.map((id, i) => i).filter((i) => prevHall[i] !== nextHall[i]);
  if (changedRanks.length !== 2) return null;
  const [a, b] = changedRanks;
  if (prevHall[a] !== nextHall[b] || prevHall[b] !== nextHall[a]) return null; // not a plain 2-rank swap
  const wyrmInstanceId = [prevHall[a], prevHall[b]].find((id) => id && getInstanceDefinition(nextState, id).id === 'monster-magma-wyrm');
  if (!wyrmInstanceId) return null;
  const otherInstanceId = prevHall[a] === wyrmInstanceId ? prevHall[b] : prevHall[a];
  const otherLabel = otherInstanceId ? titleFor(nextState, otherInstanceId) : 'an empty rank';
  return `Magma Wyrm switched ranks with ${otherLabel}.`;
}

// `state` is pre-dispatch, `nextState` is post-dispatch - some messages need
// the before (e.g. an instance about to be destroyed) and some need the
// after (e.g. combat outcome, which the resolver already computed).
function describeAction(state, nextState, type, payload) {
  const name = playerName(state, payload.playerId);
  switch (type) {
    case ACTION_TYPES.VISIT_VILLAGE: {
      const swap = describeMagmaWyrmSwap(state, nextState);
      return `${name} visited the Village.${swap ? ` ${swap}` : ''}`;
    }
    case ACTION_TYPES.BUY_CARD: {
      const player = nextState.players.find((p) => p.id === payload.playerId);
      const boughtId = player.discard[player.discard.length - 1];
      return `${name} bought ${titleFor(nextState, boughtId)}.`;
    }
    case ACTION_TYPES.LEVEL_UP_HERO:
      return `${name} leveled up ${titleFor(state, payload.heroInstanceId)}.`;
    case ACTION_TYPES.ASCEND_HERO:
      return `${name} paid 4 XP to ascend ${titleFor(state, payload.heroInstanceId)} to level 4.`;
    case ACTION_TYPES.USE_VILLAGE_ABILITY:
      return `${name} used ${titleFor(state, payload.instanceId)}'s Village ability.`;
    case ACTION_TYPES.END_VILLAGE_TURN:
      return `${name} ended their Village turn.`;
    case ACTION_TYPES.ENTER_DUNGEON:
      return `${name} entered the Dungeon.`;
    case ACTION_TYPES.EQUIP_WEAPON:
      return `${name} equipped ${titleFor(state, payload.weaponInstanceId)} on ${titleFor(state, payload.heroInstanceId)}.`;
    case ACTION_TYPES.UNEQUIP_WEAPON: {
      const player = state.players.find((p) => p.id === payload.playerId);
      const equipped = player?.equippedWeapons[payload.heroInstanceId] || [];
      // Same default as unequipWeapon itself (dungeonActions.js): the
      // most-recently-equipped weapon when the payload doesn't say which.
      const weaponInstanceId = payload.weaponInstanceId ?? equipped[equipped.length - 1];
      return weaponInstanceId
        ? `${name} unequipped ${titleFor(state, weaponInstanceId)} from ${titleFor(state, payload.heroInstanceId)}.`
        : `${name} unequipped ${titleFor(state, payload.heroInstanceId)}.`;
    }
    case ACTION_TYPES.USE_DUNGEON_ABILITY:
      return `${name} used ${titleFor(state, payload.instanceId)}'s Dungeon ability.`;
    case ACTION_TYPES.CHOOSE_MONSTER: {
      // A Battle effect may have queued a player choice (see resolveCombat.js's
      // own comment on pendingCombatContinuation) - win/loss isn't known yet
      // in that case; this dispatch's own log line stays generic, and the
      // real outcome (describeCombatOutcome) gets logged later, correctly
      // attributed to this same player, once the fight actually concludes
      // (resumeCombatIfReady's own pendingGlobalLogMessage).
      const result = nextState.meta.lastCombatResult;
      if (!result || nextState.meta.pendingCombatContinuation) return `${name} fought a monster.`;
      return `${name} ${describeCombatOutcome(result)}`;
    }
    case ACTION_TYPES.USE_SPOILS_ABILITY:
      return `${name} used ${titleFor(state, payload.instanceId)}'s Spoils ability.`;
    case ACTION_TYPES.USE_SMUGGLER_REACT:
      return `${name} destroyed the Smuggler to buy a card.`;
    case ACTION_TYPES.TAKE_FAMILIAR: {
      const player = nextState.players.find((p) => p.id === payload.playerId);
      return `${name} took ${titleFor(nextState, player.familiarInstanceId)} as a familiar.`;
    }
    case ACTION_TYPES.USE_FAMILIAR_ABILITY: {
      const player = nextState.players.find((p) => p.id === payload.playerId);
      return `${name} used ${titleFor(nextState, player.familiarInstanceId)}'s ability.`;
    }
    case ACTION_TYPES.END_DUNGEON_TURN:
      return `${name} ended their Dungeon turn.`;
    case ACTION_TYPES.PREPARE:
      return payload.orderedInstanceIds.length
        ? `${name} prepared ${payload.orderedInstanceIds.length} card(s) for next turn.`
        : `${name} prepared (no cards ordered).`;
    case ACTION_TYPES.REST:
      return `${name} rested, destroying ${titleFor(state, payload.instanceIdToDestroy)}.`;
    case ACTION_TYPES.RESOLVE_CALDERA_DRAGON_DISCARD:
      return `Caldera Dragon forced ${name} to discard ${titleFor(state, payload.instanceId)} (drew a card from an ability).`;
    case ACTION_TYPES.RESOLVE_HAND_CHOICE: {
      const pending = state.meta.pendingHandChoices?.[0];
      const verb = pending?.verb === 'discard' ? 'discard' : 'destroy';
      return `${name} was forced to ${verb} ${titleFor(state, payload.instanceId)}.`;
    }
    case ACTION_TYPES.RESOLVE_OPTIONAL_DISCARD:
      return payload.accept
        ? `${name} chose to discard cards.`
        : `${name} declined to discard and lost XP instead.`;
    case ACTION_TYPES.RESOLVE_EFFECT_CHOICE: {
      const pending = state.meta.pendingHandChoices?.[0];
      const option = pending?.options.find((o) => o.key === payload.key);
      return `${name} chose: ${option?.label || payload.key}.`;
    }
    // Matt: "increase the granularity of the logs... every action the
    // player takes should be put in the log... so that when we have bugs
    // like this, you can see the exact sequence of events." These 3 queued-
    // choice resolutions (Shield Bearer's React, Intelligent's React, Rugged
    // Jack's bonus Prepare) previously fell through to the generic "took an
    // action" default below - accurate but useless for reconstructing what
    // actually happened.
    case ACTION_TYPES.RESOLVE_SHIELD_BEARER_OFFER: {
      const pending = state.meta.pendingHandChoices?.[0];
      if (!pending) return `${name} took an action.`;
      return payload.accept
        ? `${name} destroyed ${titleFor(state, pending.shieldBearerInstanceId)} to save ${titleFor(state, pending.heroInstanceId)}.`
        : `${name} declined to sacrifice ${titleFor(state, pending.shieldBearerInstanceId)}.`;
    }
    case ACTION_TYPES.RESOLVE_INTELLIGENT_REACT_OFFER: {
      const pending = state.meta.pendingHandChoices?.[0];
      if (!pending) return `${name} took an action.`;
      return payload.accept
        ? `${name} used ${titleFor(state, pending.instanceId)}'s React ability.`
        : `${name} declined ${titleFor(state, pending.instanceId)}'s React ability.`;
    }
    case ACTION_TYPES.RESOLVE_PREPARE_PICK:
      return (payload.orderedInstanceIds || []).length
        ? `${name} prepared ${payload.orderedInstanceIds.length} card(s) for next turn (Rugged Jack).`
        : `${name} prepared (no cards ordered) (Rugged Jack).`;
    case ACTION_TYPES.SPEND_XP_TOKEN:
      return `${name} spent a ${payload.color} XP token.`;
    default:
      return `${name} took an action.`;
  }
}

function appendLog(state, nextState, type, payload) {
  let message;
  try {
    message = describeAction(state, nextState, type, payload);
    const delta = describeStateDelta(state, nextState, type, payload.playerId);
    if (delta) message = `${message} (${delta})`;
  } catch {
    return nextState; // never let a logging bug block a real action
  }
  const entry = {
    turnNumber: state.meta.turnNumber, playerId: payload.playerId, playerName: playerName(state, payload.playerId), message,
  };
  let log = [...(nextState.log || []), entry];
  let { meta } = nextState;
  // Reactive consequences of the action just logged (e.g. resumeCombatIfReady
  // concluding a fight that paused on a Battle-queued choice - see
  // resolveCombat.js's own comment) are stashed on a transient meta field
  // rather than appended where they happen, so they always read as following
  // the action that caused them instead of preceding it. { playerId,
  // message } (not a plain string) since the dispatch that actually drains
  // the queue isn't necessarily the same player the consequence belongs to
  // (e.g. an opponent answering Hellhound's own "discard a hero" choice
  // finishes resolving the FIGHTING player's own combat outcome).
  if (meta.pendingGlobalLogMessage) {
    const { playerId: msgPlayerId, message: msgText } = meta.pendingGlobalLogMessage;
    log = [...log, {
      turnNumber: state.meta.turnNumber, playerId: msgPlayerId, playerName: playerName(state, msgPlayerId), message: `${playerName(state, msgPlayerId)} ${msgText}`,
    }];
    meta = { ...meta, pendingGlobalLogMessage: null };
  }
  return { ...nextState, meta, log: log.slice(-MAX_LOG_ENTRIES) };
}

// Single reducer-style entry point: dispatch(state, action) -> newState. Every
// caller (Vuex store, AI bot, multiplayer sync layer) goes through this exact
// same function - no special-cased execution paths for any of them.
export function dispatch(state, action) {
  if (state.meta.gameOver) throw new Error('The game is already over.');
  const { type, payload = {} } = action;
  const { playerId } = payload;
  // Caldera Dragon's forced discard (see abilityActions.js's
  // applyCalderaDragonDiscard/resolveCalderaDragonDiscard) is mandatory and
  // must be resolved before anything else - only ever owed by the active
  // player mid-turn, so this can't block anyone who'd be allowed to act
  // anyway.
  if (state.meta.pendingCalderaDragonDiscard && type !== ACTION_TYPES.RESOLVE_CALDERA_DRAGON_DISCARD) {
    throw new Error('You must choose a card to discard for Caldera Dragon before doing anything else.');
  }
  // Raid/Battle/Aftermath effects that require choosing a card (rulebook,
  // "Raid Effects": "if the Raid effect involves a decision, the active
  // player makes the decision" - the same "who chooses" convention applies
  // generally) are resolved by the same pending-queue pattern - see
  // handChoiceActions.js's resolveHandChoice and effectHelpers.js's
  // queueHandChoice. Blocks EVERY action, not just the owed player's -
  // matches "effects resolve completely before play continues" (some
  // entries are owed to a non-active player, e.g. Hellhound, but the active
  // player's turn still can't proceed around them).
  if ((state.meta.pendingHandChoices || []).length
    && type !== ACTION_TYPES.RESOLVE_HAND_CHOICE && type !== ACTION_TYPES.RESOLVE_OPTIONAL_DISCARD
    && type !== ACTION_TYPES.RESOLVE_EFFECT_CHOICE && type !== ACTION_TYPES.RESOLVE_SHIELD_BEARER_OFFER
    && type !== ACTION_TYPES.RESOLVE_INTELLIGENT_REACT_OFFER && type !== ACTION_TYPES.RESOLVE_PREPARE_PICK
    && type !== ACTION_TYPES.RESOLVE_OPTIONAL_CURSE_DESTROY) {
    throw new Error('A card must be chosen for a pending effect before doing anything else.');
  }
  const innerState = dispatchInner(state, type, payload, playerId);
  // Combat resolution ordering (Matt: "the battle is being resolved before
  // battle effects are applied sometimes" - see resolveCombat.js's own
  // comment on resolveCombat/resumeCombatIfReady): if a fight paused mid-
  // resolution waiting on a Battle-queued choice (Smokeplume Drake's
  // "Discard 2 cards," etc.), this is the single choke point that notices
  // once that choice is fully answered and runs the rest of combat (win/loss,
  // Aftermath) for real. A cheap no-op on every dispatch that isn't resolving
  // the tail of a paused fight's queue. Every hand-choice resolution path -
  // human UI, the AI bot's own resolveOnePendingChoice/resolvePendingChoices,
  // the store layer's resolveAIOwedPendingChoices wrapper - already funnels
  // through this same dispatch() function, so this one hook covers all of
  // them with no changes needed anywhere else.
  const combatSettledState = resumeCombatIfReady(innerState);
  // Matt (relaying a friend's bug report): "we should make sure that the
  // trophy effect on the Warrior Bones, or really any trophy effects,
  // trigger whether they're in your hand when you draw your original hand
  // or if they're in your hand because you drew them partway through your
  // turn." VISIT_VILLAGE/ENTER_DUNGEON's own resolveTrophyEffects call only
  // ever saw the hand as it stood at that single moment - a Trophy card
  // drawn later the same turn (a hand-card ability, a Spoils reward, a hall
  // effect) never got a second look. resolveTrophyEffects is already
  // idempotent per instance (usageKey/markUsed - see its own comment), so
  // re-running it after every action for the rest of the active player's
  // Village/Dungeon turn is a cheap no-op except for genuinely new arrivals.
  const nextState = (combatSettledState.meta.phase === PHASES.VILLAGE || combatSettledState.meta.phase === PHASES.DUNGEON)
    ? resolveTrophyEffects(combatSettledState, combatSettledState.players[combatSettledState.meta.activePlayerIndex].id, combatSettledState.meta.phase)
    : combatSettledState;
  // Belac/Doomladen's Battle-effect doubling (resolveCombat.js's
  // battleRepeatCount) can queue two Salamander-style "destroy a matching
  // card" choices off the same fight, both computed against the
  // not-yet-destroyed hand - resolving the first one for real can leave the
  // second referencing a card that's already gone. VISIT_VILLAGE/
  // ENTER_DUNGEON already dropDeadEntries after resolveUnstableCrystalsCascade
  // for the identical "a later same-chain effect invalidated an
  // already-queued choice" shape (Trophy + Unstable Crystals) - CHOOSE_MONSTER
  // combat resolution turned out to have the same risk with no equivalent
  // safety net, so this drops it here instead, universally, rather than
  // hand-picking every action type that can trigger this. Was surfaced by
  // heuristicBot.js's lookahead search hitting it in self-play
  // (resolveOnePendingChoice tried to resolve a choice whose sole candidate
  // had already been destroyed) - real engine bug, reachable by a human
  // player too, not lookahead-specific. Idempotent/cheap when the head entry
  // is already valid or isn't a checkable kind (dropDeadEntries' own
  // comment), so calling it unconditionally here is safe even though most
  // action types never touch pendingHandChoices at all.
  return appendLog(state, dropDeadEntries(nextState), type, payload);
}

function dispatchInner(state, type, payload, playerId) {
  switch (type) {
    case ACTION_TYPES.VISIT_VILLAGE:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.AWAITING_ACTION);
      assertMayVisitVillage(state, playerId);
      assertNoForcedFightObligation(state, playerId);
      assertNoAssignedMonsterObligation(state, playerId);
      // dropDeadEntries at the tail: resolveTrophyEffects can queue a pending
      // choice (e.g. War Incarnate's "ignore highest-Strength hero") whose
      // candidate is still in hand at that moment, but resolveUnstableCrystals
      // Cascade's own immediate (non-queued) "discard 1 random card" - which
      // runs AFTER Trophy effects in this same chain - can then yank that
      // exact card out of hand before anyone gets a chance to answer the
      // already-queued choice. Without this, the stale entry sat in
      // pendingHandChoices referencing a card no longer in hand/discard, and
      // resolveHandChoice's own candidate check (correctly) rejected
      // whatever anyone tried to pick for it - a crash for the AI, a
      // permanently-stuck picker for a human.
      return dropDeadEntries(resolveUnstableCrystalsCascade(
        resolveVillageVisitHallEffects(
          resolveCircleOfProtectionPlacement(resolveTrophyEffects(revealHand(state, PHASES.VILLAGE), playerId), playerId),
          playerId,
        ),
        playerId,
      ));

    case ACTION_TYPES.BUY_CARD:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.VILLAGE);
      return buyCard(state, payload);

    case ACTION_TYPES.LEVEL_UP_HERO:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.VILLAGE);
      return levelUpHero(state, payload);

    case ACTION_TYPES.USE_VILLAGE_ABILITY:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.VILLAGE);
      return useVillageAbility(state, payload);

    case ACTION_TYPES.END_VILLAGE_TURN:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.VILLAGE);
      return endTurn(state);

    case ACTION_TYPES.ENTER_DUNGEON:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.AWAITING_ACTION);
      // dropDeadEntries at the tail - see the identical comment on
      // VISIT_VILLAGE above. Here the concrete trigger is
      // resolveDungeonVisitHallEffects' own Oblivion Deathkeeper handler
      // ("discard 1 random card when you enter the dungeon"), which can just
      // as easily grab a card a Trophy effect queued moments earlier in this
      // same chain (found via a seed-7035 AI self-play sweep: a queued War
      // Incarnate "ignore highest-Strength hero" choice referencing a hero
      // that Oblivion Deathkeeper's random discard had already removed from
      // hand by the time anyone tried to answer it).
      return dropDeadEntries(resolveUnstableCrystalsCascade(
        resolveDungeonVisitHallEffects(
          resolveCircleOfProtectionPlacement(resolveTrophyEffects(revealHand(state, PHASES.DUNGEON), playerId, PHASES.DUNGEON), playerId),
          playerId,
        ),
        playerId,
      ));

    case ACTION_TYPES.EQUIP_WEAPON:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return equipWeapon(state, payload);

    case ACTION_TYPES.UNEQUIP_WEAPON:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return unequipWeapon(state, payload);

    case ACTION_TYPES.USE_DUNGEON_ABILITY:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return useDungeonAbility(state, payload);

    case ACTION_TYPES.CHOOSE_MONSTER:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      // Numenera, Bandit Taskmaster/Bandit Warrior: "Global: You must use
      // all Dungeon abilities (if possible)." Fighting a monster closes the
      // Dungeon-ability window for the rest of the turn (see
      // useHandCardAbility's own foughtMonsterThisTurn check), so this is
      // one of the two points that obligation must already be satisfied.
      if (hasUnmetBanditObligation(state, playerId)) {
        throw new Error('You must use all currently-usable Dungeon abilities before fighting a monster.');
      }
      return chooseMonster(state, payload);

    case ACTION_TYPES.USE_SPOILS_ABILITY:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return useSpoilsAbility(state, payload);

    case ACTION_TYPES.USE_SMUGGLER_REACT:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return useSmugglerReact(state, payload);

    case ACTION_TYPES.TAKE_FAMILIAR:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      return takeFamiliar(state, payload);

    // Into the Abyss's 3->4 hero ascension (village/leveling.js's
    // ascendHeroToLevel4) - same spoils-window gating as TAKE_FAMILIAR
    // above (Matt: "level up to 4 for 4xp as a spoils ability"), not tied
    // to any specific card's own printed Spoils ability text.
    case ACTION_TYPES.ASCEND_HERO:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      if (!state.meta.spoilsWindowOpen) throw new Error('You may only ascend a hero to level 4 right after winning a fight.');
      return ascendHeroToLevel4(state, playerId, payload.heroInstanceId, { xpTokenColors: payload.choices?.xpTokenColors });

    // Familiar abilities can be Village- or Dungeon-timed - the specific
    // phase check against that ability's own timing happens inside
    // useFamiliarAbility itself, so this only needs to allow both.
    case ACTION_TYPES.USE_FAMILIAR_ABILITY:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.VILLAGE, PHASES.DUNGEON);
      return useFamiliarAbility(state, payload);

    case ACTION_TYPES.END_DUNGEON_TURN: {
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.DUNGEON);
      // Rulebook, "The Dungeon" (p.18): "you must choose a monster to
      // fight" - unlike visiting the village, entering the dungeon commits
      // you to a fight before your turn can end, UNLESS nobody is currently
      // fightable at all (e.g. the dungeon deck is exhausted and the hall
      // is empty) - see hasAnyFightableMonster's own comment.
      const activePlayer = state.players[state.meta.activePlayerIndex];
      if (!activePlayer.turnFlags.foughtMonsterThisTurn && hasAnyFightableMonster(state, activePlayer)) {
        throw new Error('You must fight a monster before ending your dungeon turn.');
      }
      // Numenera, Bandit Taskmaster/Bandit Warrior: "Global: You must use
      // all Dungeon abilities (if possible)." The other point that
      // obligation must already be satisfied - ending the turn without
      // fighting at all (e.g. nothing fightable) still can't skip it.
      if (hasUnmetBanditObligation(state, playerId)) {
        throw new Error('You must use all currently-usable Dungeon abilities before ending your dungeon turn.');
      }
      return endTurn(state);
    }

    // Caverns of Bane rulebook errata (correcting the base Thunderstone 7
    // rules): "When a player takes the Prepare or Rest action, Trophy
    // effects do not activate. The active player is not required to reveal
    // his or her hand." Trophy effects only fire on the two hand-revealing
    // actions (Visit the Village, Enter the Dungeon) - see those two cases
    // below.
    case ACTION_TYPES.PREPARE:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.AWAITING_ACTION);
      assertNoForcedFightObligation(state, playerId);
      assertNoAssignedMonsterObligation(state, playerId);
      assertMayRestOrPrepare(state, playerId);
      return prepare(state, payload);

    case ACTION_TYPES.REST:
      assertActivePlayer(state, playerId);
      assertPhase(state, PHASES.AWAITING_ACTION);
      assertNoForcedFightObligation(state, playerId);
      assertNoAssignedMonsterObligation(state, playerId);
      assertMayRestOrPrepare(state, playerId);
      return rest(state, payload);

    // No assertPhase - Caldera Dragon's discard can be triggered by a
    // Village- or Dungeon-timed drawing ability alike, so whichever phase
    // was active when it triggered is still the phase it must resolve in.
    case ACTION_TYPES.RESOLVE_CALDERA_DRAGON_DISCARD:
      assertActivePlayer(state, playerId);
      return resolveCalderaDragonDiscard(state, payload);

    // No assertPhase (a Raid/Battle/Aftermath effect can trigger mid-combat,
    // so whichever phase the fight happened in is still the phase it must
    // resolve in) and deliberately no assertActivePlayer either - some
    // entries (Charred Bruin, Hellhound) are owed to a non-active player,
    // who must be able to resolve their own entry on their own turn view.
    // resolveHandChoice itself checks payload.playerId against the pending
    // entry's own playerId, which is the only identity check that applies.
    case ACTION_TYPES.RESOLVE_HAND_CHOICE:
      return resolveHandChoice(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Blazing Grimalkin's
    // opt-in/decline is also owed to potentially any player, not just the
    // active one.
    case ACTION_TYPES.RESOLVE_OPTIONAL_DISCARD:
      return resolveOptionalDiscard(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Curse of War's opt-in/
    // decline is owed to whichever player used the curse's own ability.
    case ACTION_TYPES.RESOLVE_OPTIONAL_CURSE_DESTROY:
      return resolveOptionalCurseSelfDestroy(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Purple Worm's "discard
    // 2 or destroy 1 hero" is also owed to whichever player fought it.
    case ACTION_TYPES.RESOLVE_EFFECT_CHOICE:
      return resolveEffectChoice(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Shield Bearer's React
    // opt-in/decline is owed to whichever player is about to lose a hero.
    case ACTION_TYPES.RESOLVE_SHIELD_BEARER_OFFER:
      return resolveShieldBearerOffer(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Intelligent's React
    // opt-in/decline is owed to whichever player just had a card destroyed
    // during their own turn.
    case ACTION_TYPES.RESOLVE_INTELLIGENT_REACT_OFFER:
      return resolveIntelligentReactOffer(state, payload);

    // Same reasoning as RESOLVE_HAND_CHOICE above - Enhanced Rugged Jack's
    // bonus prepare action is owed to whichever player's mark just got
    // fought.
    case ACTION_TYPES.RESOLVE_PREPARE_PICK:
      return resolvePreparePick(state, payload);

    // Numenera's colored-XP rule (state/xpTokens.js): free, once per turn,
    // not gated to a specific phase - Matt: "in addition to your normal
    // Village/Dungeon action."
    case ACTION_TYPES.SPEND_XP_TOKEN:
      assertActivePlayer(state, playerId);
      return spendXpToken(state, payload);

    default:
      throw new Error(`Unknown action type: ${type}`);
  }
}
